import React from "react";
import { CloseOutlined, DownOutlined, PlusOutlined } from "@ant-design/icons";
import { Switch } from "antd";
import styled from "styled-components";
import { AppSelect } from "@/shared/components";
import { Icon } from "@/shared/components/Icon/Icon";
import { EXCLUSIVITY_CATEGORY_OPTIONS, EXCLUSIVITY_PERIOD_OPTIONS, SUGGESTED_COMPETITORS } from "./constants";
import {
	Field,
	Fields,
	InlineError,
	SectionCopy,
	SectionDescription,
	SectionHeader,
	SectionTitle,
	SectionTitleRow,
	TermsSection,
	TermsStatusBadge,
} from "./SectionPrimitives";
import type {
	EngagementTermsFormController,
	EngagementTermsSectionStatusProps,
	RegisterValidationTarget,
} from "./types";

const BrandsLabel = styled.div`
	margin-top: 24px;
	font-size: 14px;
	color: #707070;
`;

const BrandsHelp = styled.div`
	font-size: 12px;
	color: #707070;
`;

const SuggestedBrandsHelp = styled(BrandsHelp)`
	margin-top: 16px;
`;

const BrandBox = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	min-height: 46px;
	margin-top: 8px;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	background: #fff;
`;

const BrandPill = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 6px 8px 6px 12px;
	border-radius: 32px;
	background: #ebeefd;
	color: #011992;
	font-size: 12px;
`;

const RemoveBrandButton = styled.button`
	display: grid;
	place-items: center;
	width: 20px;
	height: 20px;
	padding: 0;
	border: 0;
	background: transparent;
	color: inherit;
	cursor: pointer;
`;

const BrandInput = styled.input<{ $characterCount: number }>`
	flex: 0 0 auto;
	width: ${(props) => `${Math.max(96, Math.min(240, props.$characterCount * 7 + 30))}px`};
	height: 30px;
	box-sizing: border-box;
	padding: 5px 12px;
	border: 1px dashed #8193f1;
	border-radius: 32px;
	outline: 0;
	background: #fff;
	color: #303030;
	font: 12px var(--font-family-app);
	transition:
		border-color 120ms ease,
		box-shadow 120ms ease,
		width 120ms ease;

	&::placeholder {
		color: #526dfe;
	}

	&:focus {
		border-color: #526dfe;
		box-shadow: 0 0 0 2px rgba(82, 109, 254, 0.1);
	}
`;

const Suggestions = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 8px;
`;

const Suggestion = styled.button`
	padding: 6px 12px;
	border: 1px solid #cdcdcd;
	border-radius: 32px;
	background: #fff;
	font: 12px var(--font-family-app);
	cursor: pointer;
`;

interface Props extends EngagementTermsSectionStatusProps {
	form: EngagementTermsFormController;
	registerValidationTarget: RegisterValidationTarget;
}

export const ExclusivitySection: React.FC<Props> = ({
	form,
	showStatusBadges,
	customizedFields,
	registerValidationTarget,
}) => (
	<TermsSection>
		<SectionHeader>
			<Icon src="/icon/cet-exclusivity.svg" size={24} preserveOriginalColor />
			<SectionCopy>
				<SectionTitleRow>
					<SectionTitle>Exclusivity (Optional)</SectionTitle>
					{showStatusBadges && <TermsStatusBadge field="exclusivity" customizedFields={customizedFields} />}
				</SectionTitleRow>
				<SectionDescription>Restrictions on competing brand work</SectionDescription>
			</SectionCopy>
			<Switch checked={form.exclusivityEnabled} onChange={form.setExclusivityEnabled} />
		</SectionHeader>
		{form.exclusivityEnabled && (
			<>
				<Fields>
					<Field
						ref={registerValidationTarget("exclusivityPeriod")}
						$invalid={!!form.validationErrors.exclusivityPeriod}
					>
						Exclusivity Period
						<AppSelect
							value={form.exclusivityPeriod || undefined}
							placeholder="Select period"
							onChange={(value) => {
								form.setExclusivityPeriod(value);
								form.clearValidationError("exclusivityPeriod");
							}}
							options={EXCLUSIVITY_PERIOD_OPTIONS.map((value) => ({ value, label: value }))}
							suffixIcon={<DownOutlined />}
						/>
						{form.validationErrors.exclusivityPeriod && (
							<InlineError>{form.validationErrors.exclusivityPeriod}</InlineError>
						)}
					</Field>
					<Field>
						Exclusivity Category
						<AppSelect
							value={form.exclusivityCategory || undefined}
							placeholder="Select category"
							onChange={form.setExclusivityCategory}
							options={EXCLUSIVITY_CATEGORY_OPTIONS.map((value) => ({ value, label: value }))}
							suffixIcon={<DownOutlined />}
						/>
					</Field>
				</Fields>
				<BrandsLabel>Restricted Brands</BrandsLabel>
				<BrandsHelp>The creator can't endorse these during the exclusivity period.</BrandsHelp>
				<BrandBox>
					{form.brands.map((brand) => (
						<BrandPill key={brand}>
							{brand}
							<RemoveBrandButton
								type="button"
								aria-label={`Remove ${brand}`}
								onClick={() => form.removeBrand(brand)}
							>
								<CloseOutlined />
							</RemoveBrandButton>
						</BrandPill>
					))}
					<BrandInput
						$characterCount={Math.max(11, form.brandDraft.length)}
						value={form.brandDraft}
						onChange={(event) => form.setBrandDraft(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								form.addBrand(form.brandDraft);
							}
						}}
						placeholder="Type to add"
					/>
				</BrandBox>
				<SuggestedBrandsHelp>
					Suggested competitors in <strong>{form.exclusivityCategory}</strong>.
				</SuggestedBrandsHelp>
				<Suggestions>
					{SUGGESTED_COMPETITORS.filter((brand) => !form.brands.includes(brand)).map((brand) => (
						<Suggestion key={brand} type="button" onClick={() => form.addBrand(brand)}>
							<PlusOutlined /> {brand}
						</Suggestion>
					))}
				</Suggestions>
			</>
		)}
	</TermsSection>
);
